import { FC, useState } from 'react';
import { Trans } from 'react-i18next';
import styled from 'styled-components';
import { CreditPack } from '../ApiTypes';
import { checkoutCreditPack } from '../api';
import { MiniCircleLoading } from '../styles/Loadings';

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1000;
  background-color: rgba(0, 0, 0, 0.75);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
`;

const Dialog = styled.div`
  background-color: #1f1f1f;
  border: 1px solid #3a3a3a;
  border-radius: 16px;
  padding: 24px;
  width: 100%;
  max-width: 420px;
  color: #f0f0f0;
`;

const Title = styled.div`
  font-size: 18px;
  font-weight: 700;
`;

const Description = styled.div`
  margin-top: 8px;
  font-size: 14px;
  color: #a8a8a8;
  line-height: 1.5;
`;

const PrimaryButton = styled.button`
  margin-top: 20px;
  width: 100%;
  height: 52px;
  border: none;
  border-radius: 26px;
  background-color: rgb(254, 167, 0);
  color: black;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;

  &:hover {
    background-color: #f8be53;
  }
`;

const PackList = styled.div`
  margin-top: 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const PackButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
  padding: 14px 16px;
  border: 1px solid #4a4a4a;
  border-radius: 12px;
  background-color: #2b2b2b;
  color: #f0f0f0;
  font-size: 15px;
  cursor: pointer;

  &:hover {
    border-color: rgb(254, 167, 0);
    background-color: #333333;
  }
`;

const PackLabel = styled.span`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const PackCredits = styled.span`
  font-weight: 700;
`;

const DiscountBadge = styled.span`
  padding: 2px 8px;
  border-radius: 999px;
  background-color: rgb(254, 167, 0);
  color: black;
  font-size: 11px;
  font-weight: 700;
  white-space: nowrap;
`;

const PackPrice = styled.span`
  text-align: right;
`;

const PackUnit = styled.div`
  font-size: 12px;
  color: #8fbf6a;
`;

const StrikePrice = styled.span`
  color: #8a8a8a;
  text-decoration: line-through;
  margin-right: 6px;
  font-size: 13px;
`;

const CancelButton = styled.button`
  margin-top: 14px;
  width: 100%;
  border: none;
  background: none;
  color: #8a8a8a;
  font-size: 14px;
  cursor: pointer;
`;

/** パックの選択肢。ダイアログとアカウント画面の両方から使う。 */
export const CreditPackList: FC<{ packs: CreditPack[] }> = (props) => {
  const [loadingPackId, setLoadingPackId] = useState<string|null>(null);

  const buyPack = async (pack: CreditPack) => {
    setLoadingPackId(pack.packId);
    try {
      const result = await checkoutCreditPack(pack.packId);
      if (result.actionUrl) {
        window.location.href = result.actionUrl;
      } else {
        window.location.href = `/credit_packs/${result.purchaseId}`;
      }
    } catch (exception) {
      setLoadingPackId(null);
    }
  };

  return <PackList>
    {props.packs.map(pack => <PackButton key={pack.packId} disabled={loadingPackId != null} onClick={() => buyPack(pack)}>
      <PackLabel>
        <PackCredits>{pack.credits} <Trans>videos</Trans></PackCredits>
        {pack.discountPercent != null && pack.discountPercent > 0 &&
          <DiscountBadge>{pack.discountPercent}% <Trans>OFF</Trans></DiscountBadge>}
      </PackLabel>
      <PackPrice>
        {loadingPackId == pack.packId ? <MiniCircleLoading /> : <>
          {pack.unitPriceUsd != null && <StrikePrice>${(pack.unitPriceUsd * pack.credits).toFixed(2)}</StrikePrice>}
          ${pack.amountUsd.toFixed(2)}
          <PackUnit>${pack.unitAmountUsd.toFixed(2)} <Trans>per video</Trans></PackUnit>
        </>}
      </PackPrice>
    </PackButton>)}
  </PackList>;
};

/**
 * まとめ買いの提案。タイルを押した直後に出す場合は `onBuySingle` を渡し、
 * 単発購入を主ボタンに置いて 1 タップの導線を塞がない。
 * 能動的に開いた場合（カタログの「まとめ買い」）は `onBuySingle` を渡さない。
 */
const CreditPackOffer: FC<{
  packs: CreditPack[],
  onBuySingle?(): void,
  onClose(): void
}> = (props) => {
  return <Backdrop onClick={props.onClose}>
    <Dialog onClick={event => event.stopPropagation()}>
      <Title><Trans>Keep going with her</Trans></Title>
      <Description>
        <Trans>Buy videos in bulk and generate instantly, without paying each time.</Trans>
      </Description>
      {props.onBuySingle && <PrimaryButton onClick={props.onBuySingle}>
        <Trans>Just this one video</Trans>
      </PrimaryButton>}
      <CreditPackList packs={props.packs} />
      <CancelButton onClick={props.onClose}><Trans>Not now</Trans></CancelButton>
    </Dialog>
  </Backdrop>;
};

export default CreditPackOffer;
